import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import SelectField from "app/static/frontend/forms/components/SelectField"

// ARIA live region for announcing combobox state changes
const AriaLiveRegion = styled.div`
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
`

/**
 * Enhanced SelectField component with improved keyboard navigation for comboboxes
 * Implements WCAG 2.1 AA accessibility requirements for dropdown/combobox controls
 *
 * Features:
 * - Enhanced keyboard navigation (Arrow keys, Enter, Escape, Type to search)
 * - Screen reader announcements for state changes
 * - ARIA live regions for dynamic content
 * - Proper focus management
 *
 * @param {Object} props - Component props
 * @param {string} props.accessibilityId - Unique ID for accessibility attributes
 * @param {string} props.label - Field label for screen readers
 * @param {Array} props.choices - Array of options {value, label}
 * @param {boolean} props.multi - Whether this is a multi-select
 * @param {*} props.value - Current selected value(s)
 * @param {Object} props.meta - Redux form meta object
 * @param {boolean} props.displayErrorWithoutTouch - Whether to show errors immediately
 * @param {string} props.tooltipText - Tooltip/description text
 * @param {Object} props.input - Redux form input object
 * @param {Function} props.onChange - Change handler function
 */
const AccessibleSelectField = (props) => {
    const {
        accessibilityId,
        label,
        choices = [],
        multi,
        value,
        meta,
        displayErrorWithoutTouch,
        tooltipText,
        ...restProps
    } = props

    const [announcement, setAnnouncement] = useState("")
    const liveRegionId = `${accessibilityId}-live`

    // Handle value changes for announcements
    const handleChange = (newValue) => {
        // Call the original onChange handler
        if (props.input?.onChange) {
            props.input.onChange(newValue)
        } else if (props.onChange) {
            props.onChange(newValue)
        }

        // Announce selection changes for screen readers
        if (multi) {
            const selectedCount = newValue ? (Array.isArray(newValue) ? newValue.length : 0) : 0
            setAnnouncement(`${selectedCount} options selected.`)
        } else {
            const selectedLabel = newValue
                ? choices.find((choice) => choice.value === newValue)?.label || newValue
                : "No selection"
            setAnnouncement(`${selectedLabel} selected.`)
        }
    }

    // Handle menu state changes
    const handleMenuOpen = () => {
        setAnnouncement(`Menu opened. ${choices.length} options available.`)
        if (restProps.onMenuOpen) {
            restProps.onMenuOpen()
        }
    }

    const handleMenuClose = () => {
        setAnnouncement("Menu closed.")
        if (restProps.onMenuClose) {
            restProps.onMenuClose()
        }
    }

    // Clear announcement after it's been read by screen readers
    useEffect(() => {
        if (announcement) {
            const timer = setTimeout(() => setAnnouncement(""), 1500)
            return () => clearTimeout(timer)
        }
    }, [announcement])

    return (
        <>
            <SelectField
                {...restProps}
                accessibilityId={accessibilityId}
                label={label}
                choices={choices}
                multi={multi}
                value={value}
                meta={meta}
                displayErrorWithoutTouch={displayErrorWithoutTouch}
                tooltipText={tooltipText}
                onChange={handleChange}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                // Essential props for enhanced keyboard navigation
                isSearchable={true} // Enable keyboard search for better navigation
                menuShouldScrollIntoView={true} // Ensure menu is visible when opened
            />

            {/* ARIA live region for dynamic announcements */}
            <AriaLiveRegion id={liveRegionId} role="status" aria-live="polite" aria-atomic="true">
                {announcement}
            </AriaLiveRegion>
        </>
    )
}

AccessibleSelectField.propTypes = {
    accessibilityId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    choices: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        }),
    ),
    multi: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
    meta: PropTypes.object,
    displayErrorWithoutTouch: PropTypes.bool,
    tooltipText: PropTypes.string,
    input: PropTypes.object,
    onChange: PropTypes.func,
    onMenuOpen: PropTypes.func,
    onMenuClose: PropTypes.func,
}

AccessibleSelectField.defaultProps = {
    choices: [],
    multi: false,
    displayErrorWithoutTouch: true,
}

export default AccessibleSelectField
